import { useContext, useEffect, useState } from "react";
import { DatePicker } from "@mui/x-date-pickers";
import dayjs, { Dayjs } from "dayjs";
import { BreadAccount } from "breadcommon";
import { UserContext } from "../firebaseio/UserContext";
import { firestoreUpdateAccount } from "../firebaseio/firestoreIo";
import { RyeModalContent } from "../rye/RyeModal";
import { RyeIcon } from "../rye/RyeIcon";
import { buildClasses } from "../utils/buildClasses";

type SyncStartChoice = "lastMonth" | "yearStart";

const SYNC_START_PRESETS: {
  id: SyncStartChoice;
  label: string;
  date: () => Dayjs;
}[] = [
  // The first is the default, and matches the date the backend gives new
  // accounts.
  {
    id: "lastMonth",
    label: "Last month",
    date: () => dayjs().subtract(1, "month").startOf("day"),
  },
  {
    id: "yearStart",
    label: "Start of this year",
    date: () => dayjs().startOf("year"),
  },
];

/**
 * Asks once, right after Plaid, when to start syncing the newly found accounts,
 * and sets that date on all of them before they're confirmed. The confirmation
 * screen still lets the user change it for individual accounts.
 */
export function SyncStartModalContent({
  candidateAccounts,
  expectedCount,
  onCancel,
  onContinue,
}: {
  /** The new accounts awaiting confirmation. */
  candidateAccounts: BreadAccount[];
  /**
   * How many to wait for, when known. New accounts reach the app through the
   * accounts listener a moment after Plaid returns; without a count, waiting
   * for at least one is the best available.
   */
  expectedCount: number | null;
  onCancel: () => void;
  /** Called once the date is saved on every candidate account. */
  onContinue: () => void;
}): JSX.Element {
  const user = useContext(UserContext);
  const [choice, setChoice] = useState<SyncStartChoice | "custom">(
    SYNC_START_PRESETS[0].id,
  );
  const [customDate, setCustomDate] = useState<Dayjs | null>(() =>
    SYNC_START_PRESETS[0].date(),
  );
  const [isSaving, setIsSaving] = useState(false);
  const syncStart =
    choice === "custom"
      ? customDate
      : SYNC_START_PRESETS.find((preset) => preset.id === choice)!.date();
  const isValidDate =
    syncStart !== null && syncStart.isValid() && !syncStart.isAfter(dayjs());
  const allCandidatesLoaded =
    candidateAccounts.length > 0 &&
    (expectedCount === null || candidateAccounts.length >= expectedCount);

  // Save once the user has continued and every candidate has arrived, so none
  // is left on the default date.
  useEffect(() => {
    if (!isSaving || !allCandidatesLoaded || syncStart === null) return;
    let cancelled = false;
    Promise.allSettled(
      candidateAccounts.map((account) =>
        firestoreUpdateAccount(user.uid, account.id, {
          manual_data: { sync_start_timestamp_secs: syncStart.unix() },
        }),
      ),
    ).then(() => {
      if (!cancelled) onContinue();
    });
    return () => {
      cancelled = true;
    };
    // Only when saving starts or the candidates finish arriving; the writes
    // themselves update candidateAccounts.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSaving, allCandidatesLoaded]);

  return (
    <RyeModalContent
      title="How far back should we sync?"
      showCloseX={false}
      content={
        <div className={buildClasses("w-[32rem]", "flex", "flex-col")}>
          <div className={buildClasses("text-sm", "text-on-surface-600")}>
            We'll import transactions for your new accounts from this date
            forward.
          </div>
          <div
            role="radiogroup"
            aria-label="Sync start date"
            className={buildClasses("mt-5", "flex", "flex-col", "gap-2")}
          >
            {SYNC_START_PRESETS.map((preset) => (
              <SyncStartOption
                key={preset.id}
                label={preset.label}
                isSelected={choice === preset.id}
                disabled={isSaving}
                onSelect={() => setChoice(preset.id)}
              >
                <span className={buildClasses("text-on-surface-500")}>
                  {preset.date().format("MMM D, YYYY")}
                </span>
              </SyncStartOption>
            ))}
            <SyncStartOption
              label="Custom date"
              isSelected={choice === "custom"}
              disabled={isSaving}
              onSelect={() => setChoice("custom")}
            >
              <div
                className={buildClasses("w-[150px]")}
                // Picking a date in the field also chooses this option.
                onClick={(e) => {
                  e.stopPropagation();
                  setChoice("custom");
                }}
              >
                <DatePicker
                  value={customDate}
                  onChange={(date) => {
                    setCustomDate(date);
                    setChoice("custom");
                  }}
                  format="MM/DD/YY"
                  disableFuture
                  disabled={isSaving}
                  slotProps={{
                    textField: {
                      size: "small",
                      inputProps: { "aria-label": "Custom sync start date" },
                    },
                    popper: {
                      // Above RyeModal's layer.
                      sx: { zIndex: 1000000001 },
                    },
                  }}
                />
              </div>
            </SyncStartOption>
          </div>
          <div
            className={buildClasses(
              "mt-5",
              "flex",
              "items-start",
              "gap-2",
              "text-xs",
              "text-on-surface-500",
              "leading-relaxed",
            )}
          >
            <RyeIcon
              name="info"
              size="xs"
              className={buildClasses("mt-0.5", "shrink-0")}
            />
            <div>
              How far back transactions are available varies by institution, so
              we'll import everything available after the date you choose.{" "}
              <span
                className={buildClasses("font-medium", "text-on-surface-700")}
              >
                Note, transactions are often only available for the past 30
                days.
              </span>
            </div>
          </div>
        </div>
      }
      actionButtons={[
        {
          text: "Cancel",
          vibe: "subdued",
          variant: "outlined",
          action: onCancel,
          close: false,
          disabled: isSaving,
        },
        {
          text: isSaving ? "Saving..." : "Continue",
          vibe: "primary",
          variant: "filled",
          action: () => setIsSaving(true),
          close: false,
          disabled: !isValidDate || isSaving,
        },
      ]}
    />
  );
}

function SyncStartOption({
  label,
  isSelected,
  disabled,
  onSelect,
  children,
}: {
  label: string;
  isSelected: boolean;
  disabled: boolean;
  onSelect: () => void;
  /** Shown on the right: the date this option means, or a field to set it. */
  children: React.ReactNode;
}): JSX.Element {
  return (
    <div
      role="radio"
      aria-checked={isSelected}
      aria-label={label}
      aria-disabled={disabled}
      tabIndex={disabled ? -1 : 0}
      onClick={() => !disabled && onSelect()}
      onKeyDown={(e) => {
        // Leave keys typed into the custom date field alone.
        if (e.target !== e.currentTarget) return;
        if (!disabled && (e.key === " " || e.key === "Enter")) {
          e.preventDefault();
          onSelect();
        }
      }}
      className={buildClasses(
        "flex",
        "items-center",
        "justify-between",
        "gap-4",
        "min-h-12",
        "px-4",
        "py-2",
        "border",
        "rounded-lg",
        "text-sm",
        "select-none",
        "transition-colors",
        "outline-none",
        "focus-visible:ring-2",
        "focus-visible:ring-purple",
        {
          if: isSelected,
          then: buildClasses("border-purple", "bg-purple-50"),
          else: buildClasses(
            "border-on-surface-200",
            "hover:border-on-surface-300",
          ),
        },
        disabled ? "cursor-default" : "cursor-pointer",
      )}
    >
      <div className={buildClasses("flex", "items-center", "gap-3")}>
        <span
          aria-hidden="true"
          className={buildClasses(
            "flex",
            "items-center",
            "justify-center",
            "w-4",
            "h-4",
            "rounded-full",
            "border",
            "shrink-0",
            isSelected ? "border-purple" : "border-on-surface-300",
          )}
        >
          {isSelected && (
            <span
              className={buildClasses(
                "w-2",
                "h-2",
                "rounded-full",
                "bg-purple",
              )}
            />
          )}
        </span>
        <span className={buildClasses("text-on-surface-800")}>{label}</span>
      </div>
      {children}
    </div>
  );
}
